import type { CONFIG_SCHEMA } from "./screenConfigSlice";
import type {
  ModuleConfig,
  ScreenTypesCollection,
  ModesCollection,
  VisualisationTypeCollection,
} from "./moduleConfig.types";

// ─── Public parameter types ───────────────────────────────────────────────────

/** Valid screen-type names (excludes the shared `config` baseline key). */
export type ScreenTypeName = keyof Omit<ScreenTypesCollection, "config">;

/** Valid mode names (excludes the shared `config` baseline key). */
export type ModeName = keyof Omit<ModesCollection, "config">;

/** Valid visualisation-type names (excludes the shared `config` baseline key). */
export type VisualisationTypeName = keyof Omit<
  VisualisationTypeCollection,
  "config"
>;

// ─── Helper ───────────────────────────────────────────────────────────────────

/**
 * Resolves a flat {@link CONFIG_SCHEMA} by walking the hierarchical config
 * tree and merging each layer on top of the previous one.
 *
 * Resolution order (later layers override earlier ones):
 *   1. `module.config`
 *   2. `screenTypes.config`
 *   3. `screenTypes.[screenType].config`
 *   4. `screenTypes.[screenType].modes.config`
 *   5. `screenTypes.[screenType].modes.[mode].config`
 *   6. `screenTypes.[screenType].modes.[mode].visualisationType.config`
 *   7. `screenTypes.[screenType].modes.[mode].visualisationType.[visualizationType].config`
 *
 * Any layer that does not exist in the config object is simply skipped.
 * Parameters are optional – omitting a deeper parameter stops resolution
 * at the corresponding layer.
 *
 * @example
 * const cfg = resolveModuleConfig(equityModuleConfig, "BRAND", "POINT_IN_TIME", "PROFILE");
 */
export function resolveModuleConfig(
  moduleConfig: ModuleConfig,
  screenType?: ScreenTypeName,
  mode?: ModeName,
  visualizationType?: VisualisationTypeName,
): CONFIG_SCHEMA {
  const { config: moduleBaseConfig, screenTypes } = moduleConfig;

  // ── Layer 1: module-level config ─────────────────────────────────────────
  const moduleConfig_ = moduleBaseConfig;

  // ── Layer 2: screenTypes.[screenType].config ──────────────────────────────
  const screenTypeConfig =
    screenType && screenTypes?.[screenType]
      ? screenTypes[screenType]!.config
      : undefined;

  // ── Layer 3: screenTypes.[screenType].modes.[mode].config ─────────────────
  const modeConfig =
    screenType && mode && screenTypes?.[screenType]?.modes?.[mode]
      ? screenTypes[screenType]!.modes![mode]!.config
      : undefined;

  // ── Layer 4: screenTypes.[screenType].modes.[mode].visualisationType.[visualizationType].config
  const vizTypeConfig =
    screenType && mode && visualizationType &&
    screenTypes?.[screenType]?.modes?.[mode]?.visualisationType?.[visualizationType]
      ? screenTypes[screenType]!.modes![mode]!.visualisationType![visualizationType]!.config
      : undefined;

  // Merge all layers in order: module → screenType → mode → visualizationType
  return {
    ...moduleConfig_,
    ...screenTypeConfig,
    ...modeConfig,
    ...vizTypeConfig,
  };
}